import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Building2, Users, Package, Briefcase, LogOut, BarChart3, TrendingUp, Settings, FileText, Car, CheckCircle, Truck, Calendar } from 'lucide-react';
import TenantHome from '../components/TenantHome';
import CustomersManagementEnhanced from '../components/CustomersManagementEnhanced';
import CustomerDetailView from '../components/CustomerDetailView';
import ServicesManagementEnhanced from '../components/ServicesManagementEnhanced';
import QuotesManagement from '../components/QuotesManagement';
import OrdersManagement from '../components/OrdersManagement';
import JobsManagement from '../components/JobsManagement';
import VehiclesManagement from '../components/VehiclesManagement';
import DriversManagement from '../components/DriversManagement';
import DriversCalendar from '../components/DriversCalendar';
import TenantSettings from '../components/TenantSettings';
import SubscriptionCard from '../components/SubscriptionCard';
import InsightsDashboard from '../components/InsightsDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantDashboard = ({ currentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [companySettings, setCompanySettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company/settings`, getAuthHeaders());
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      // Default to no driver module if error
      setCompanySettings({ feature_flags: {} });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const isDriverModuleEnabled = companySettings?.feature_flags?.driver_module_enabled || false;

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Use useMemo to recalculate navItems when driver module status changes
  const navItems = React.useMemo(() => [
    { id: 'overview', label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
    { id: 'insights', label: 'Insights', icon: TrendingUp, path: '/dashboard/insights' },
    { id: 'customers', label: 'Customers', icon: Users, path: '/dashboard/customers' },
    { id: 'vehicles', label: 'Vehicles', icon: Car, path: '/dashboard/vehicles' },
    { id: 'services', label: 'Services', icon: Package, path: '/dashboard/services' },
    { id: 'quotes', label: 'Quotes', icon: FileText, path: '/dashboard/quotes' },
    { id: 'orders', label: 'Orders', icon: CheckCircle, path: '/dashboard/orders' },
    { id: 'jobs', label: 'Jobs', icon: Briefcase, path: '/dashboard/jobs' },
    // Conditionally add driver menu items
    ...(isDriverModuleEnabled ? [
      { id: 'drivers', label: 'Drivers', icon: Truck, path: '/dashboard/drivers' },
      { id: 'drivers-calendar', label: 'Drivers Calendar', icon: Calendar, path: '/dashboard/drivers-calendar' },
    ] : [])
  ], [isDriverModuleEnabled]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Tenant Portal</h1>
                <p className="text-xs text-gray-500">Job & Customer Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium" data-testid="tenant-user-name">{currentUser.full_name}</p>
                <p className="text-xs text-gray-500">{currentUser.role_type}</p>
              </div>
              <Link to="/dashboard/settings">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="gap-2 p-2"
                  title="Settings"
                  data-testid="settings-button"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onLogout}
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen bg-white border-r border-gray-200 p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setActiveTab(item.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <Routes>
            <Route index element={<TenantHome getAuthHeaders={getAuthHeaders} />} />
            <Route path="insights" element={<InsightsDashboard getAuthHeaders={getAuthHeaders} />} />
            <Route path="customers" element={<CustomersManagementEnhanced getAuthHeaders={getAuthHeaders} />} />
            <Route path="customers/:customerId" element={<CustomerDetailView getAuthHeaders={getAuthHeaders} />} />
            <Route path="vehicles" element={<VehiclesManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="services" element={<ServicesManagementEnhanced getAuthHeaders={getAuthHeaders} />} />
            <Route path="quotes" element={<QuotesManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="orders" element={<OrdersManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="jobs" element={<JobsManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="drivers" element={<DriversManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="drivers-calendar" element={<DriversCalendar getAuthHeaders={getAuthHeaders} />} />
            <Route path="settings" element={<TenantSettings getAuthHeaders={getAuthHeaders} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default TenantDashboard;
